// React Components
import * as React from 'react';
import { useState, useEffect } from "react";
import axios from "axios";

// Material UI Components
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

// Audit Vault Components
import ProgressBar from '../common/ProgressBar';
import ServiceIsDown from "../common/ServiceIsDown";
import JobHistoryAccordion from './JobHistoryAccordion';

// Audit Vault Utilities
import { encryptKey } from '../../utilities/common-encrypt-util';

const WEB_API_URL = process.env.REACT_APP_WEB_API_URL;

export default function JobsList(props) {
  const { selectedTenant } = props;

  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [serviceIsDownError, setServiceIsDownError] = useState(false);

  const [loadingSharePointJob, setLoadingSharePoint] = useState(true);
  const [loadingExchangeJob, setLoadingExchange] = useState(true);
  const [loadingEntraIDJob, setLoadingEntraID] = useState(true);
  const [loadingGeneralJob, setLoadingGeneral] = useState(true);

  const [sharePointJobHistory, setSharePointJobHistory] = useState([]);
  const [exchangeJobHistory, setExchangeJobHistory] = useState([]);
  const [entraIDJobHistory, setEntraIDJobHistory] = useState([]);
  const [generalJobHistory, setGeneralJobHistory] = useState([]);

  const [sharePointAccordian, setSharePointAccordian] = useState(false);
  const [exchangeAccordian, setExchangeAccordian] = useState(false);
  const [entraIDAccordian, setEntraIDAccordian] = useState(false);
  const [generalAccordian, setGeneralAccordian] = useState(false);

  // Need page variables for each audit type
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const [page2, setPage2] = React.useState(0);
  const [rowsPerPage2, setRowsPerPage2] = React.useState(25);

  const [page3, setPage3] = React.useState(0);
  const [rowsPerPage3, setRowsPerPage3] = React.useState(25);

  const [page4, setPage4] = React.useState(0);
  const [rowsPerPage4, setRowsPerPage4] = React.useState(25);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangePage2 = (event, newPage) => {
    setPage2(newPage);
  };

  const handleChangePage3 = (event, newPage) => {
    setPage3(newPage);
  };

  const handleChangePage4 = (event, newPage) => {
    setPage4(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage2 = (event) => {
    setRowsPerPage2(+event.target.value);
    setPage2(0);
  };

  const handleChangeRowsPerPage3 = (event) => {
    setRowsPerPage3(+event.target.value);
    setPage3(0);
  };

  const handleChangeRowsPerPage4 = (event) => {
    setRowsPerPage4(+event.target.value);
    setPage4(0);
  };

  async function fetchJobHistory(job) {
    if (selectedTenant && job) {
      try {
        switch (Number(job.auditTypeAssigned)) {
          case 0:
            setLoadingSharePoint(true);
            break;
          case 1:
            setLoadingExchange(true);
            break;
          case 2:
            setLoadingEntraID(true);
            break;
          case 4:
            setLoadingGeneral(true);
            break;
          default:
            break;
        }

        const rsaKey = `{Id: "${await encryptKey(selectedTenant.toString())}", JobId: "${await encryptKey(job.id.toString())}" }`;

        if (rsaKey) {
          const result = await axios.get(`${WEB_API_URL}Jobs/GetJobHistoryForLastMonth`, {
            params: {
              key: `${rsaKey}`
            }
          });

          if (result.data != null) {
            switch (Number(job.auditTypeAssigned)) {
              case 0:
                setSharePointJobHistory(result.data);
                break;
              case 1:
                setExchangeJobHistory(result.data);
                break;
              case 2:
                setEntraIDJobHistory(result.data);
                break;
              case 4:
                setGeneralJobHistory(result.data);
                break;
              default:
                break;
            }
          }
        }
      } catch (e) {
        console.log("ERROR: JobsList.fetchJobHistory");
        console.log(e);
        setServiceIsDownError(true);
      } finally {
        setLoadingSharePoint(false);
        setLoadingExchange(false);
        setLoadingEntraID(false);
        setLoadingGeneral(false);
      }
    }
  }

  const handleAccordionLoad = async (job) => {
    switch (Number(job.auditTypeAssigned)) {
      case 0:
        if (!sharePointAccordian) {
          fetchJobHistory(job);
        }
        setSharePointAccordian(!sharePointAccordian);
        break;

      case 1:
        if (!exchangeAccordian) {
          fetchJobHistory(job);
        }
        setExchangeAccordian(!exchangeAccordian);
        break;

      case 2:
        if (!entraIDAccordian) {
          fetchJobHistory(job);
        }
        setEntraIDAccordian(!entraIDAccordian);
        break;

      case 4:
        if (!generalAccordian) {
          fetchJobHistory(job);
        }
        setGeneralAccordian(!generalAccordian);
        break;

      default:
        break;
    }
  };

  async function fetchJobsData() {
    try {
      setLoading(true);

      if (selectedTenant) {
        const rsaKey = `{Id: "${await encryptKey(selectedTenant.toString())}" }`;

        if (rsaKey) {
          const result = await axios.get(`${WEB_API_URL}Jobs/GetJobsByTenantId`, {
            params: {
              key: `${rsaKey}`
            }
          });

          if (result.data != null) {
            setJobs(result.data);
          }
        }
      }
    } catch (e) {
      console.log("ERROR: MyTenant.fetchJobsData");
      console.log(e);
      setServiceIsDownError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchJobsData();
  }, []);

  if (serviceIsDownError) {
    return <ServiceIsDown />;
  }

  const jobConfigByType = {
    0: {
      title: 'SharePoint',
      expanded: sharePointAccordian,
      loading: loadingSharePointJob,
      history: sharePointJobHistory,
      page,
      rowsPerPage,
      onPageChange: handleChangePage,
      onRowsPerPageChange: handleChangeRowsPerPage,
    },
    1: {
      title: 'Exchange',
      expanded: exchangeAccordian,
      loading: loadingExchangeJob,
      history: exchangeJobHistory,
      page: page2,
      rowsPerPage: rowsPerPage2,
      onPageChange: handleChangePage2,
      onRowsPerPageChange: handleChangeRowsPerPage2,
    },
    2: {
      title: 'Entra ID',
      expanded: entraIDAccordian,
      loading: loadingEntraIDJob,
      history: entraIDJobHistory,
      page: page3,
      rowsPerPage: rowsPerPage3,
      onPageChange: handleChangePage3,
      onRowsPerPageChange: handleChangeRowsPerPage3,
    },
    4: {
      title: 'General',
      expanded: generalAccordian,
      loading: loadingGeneralJob,
      history: generalJobHistory,
      page: page4,
      rowsPerPage: rowsPerPage4,
      onPageChange: handleChangePage4,
      onRowsPerPageChange: handleChangeRowsPerPage4,
    },
  };

  return (
    <>
      <Card
        key="jobsList"
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: 1000,
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <ProgressBar message="Loading ..." loading={loading} />

          {jobs && jobs.map((row) => {
            const config = jobConfigByType[Number(row.auditTypeAssigned)];
            if (!config) return null;

            return (
              <JobHistoryAccordion
                key={`${config.title}-${row.id}`}
                job={row}
                title={config.title}
                expanded={config.expanded}
                loading={config.loading}
                history={config.history}
                page={config.page}
                rowsPerPage={config.rowsPerPage}
                onToggle={handleAccordionLoad}
                onPageChange={config.onPageChange}
                onRowsPerPageChange={config.onRowsPerPageChange}
              />
            );
          })}
        </CardContent>
      </Card>
    </>
  );
}